import {
  ExtractFromSegmentProfiler,
  FallbackProfiler,
  GraphHopperRouter,
  SwisstopoProfiler,
  TrackManager,
  UnsnappedDensifier,
} from "@geoblocks/edittrack";
import { doubleClick, singleClick } from "ol/events/condition";
import type Feature from "ol/Feature";
import type LineString from "ol/geom/LineString.js";
import VectorLayer from "ol/layer/Vector";
import type OLMap from "ol/Map";
import VectorSource from "ol/source/Vector";
import type View from "ol/View";
import {
  createDetour,
  type DetourLineType,
  detourLineTypes,
  fetchDetour,
  partByLineType,
  readClosedWayGeometries,
  readDetourGeometries,
  updateDetour,
  writeClosedWayGeometries,
  writeDetourGeometries,
} from "./api-detour";
import { detourStyle } from "./style/detour";
import { walkingAscentDescent, walkingTime } from "./walking";

export type DetourEditorOptions = {
  /**
   * An OpenLayers map instance
   */
  map: OLMap;
  /**
   * The optional uuid of the detour to edit
   */
  uuid?: string;
};

type DetourProperties = {
  closedWay: {
    time: number;
    distance: number;
    ascent: number;
    descent: number;
  };
  detour: {
    time: number;
    distance: number;
    ascent: number;
    descent: number;
  };
};

/**
 * # DetourEditor
 *
 * ## Demos
 *  - [Create or edit existing detour](/demo/detour-editor-base.html)
 *
 * ## Description
 *
 * The DetourEditor is responsible for creating and editing detours
 *
 * To construct a new DetourEditor, you need to provide an OpenLayers map instance:
 * ```javascript
 * const detourEditor = new sww.DetourEditor({
 *   map: map
 * });
 *```
 *
 * This will allow the user to create two types of lines:
 * - `closed_way_line`: The closed way line
 * - `detour_line`: The detour line
 *
 * To edit an existing detour, you can provide the uuid of the detour:
 * ```javascript
 * const detourEditor = new sww.DetourEditor({
 *   map: map,
 *   uuid: "1234-5678-91011"
 * });
 * ```
 * When the detour is loaded, a `detour-loaded` event is fired. To listen to this event, you can use the following code:
 * ```javascript
 * detourEditor.addEventListener("detour-loaded", () => {
 *  console.log("Detour loaded");
 * });
 * ```
 *
 * ## Create or update the detour to the server
 *
 * To save the detour to the server, call the {@link DetourEditor.save} function.
 * The detour line and the closed way line can be removed by setting their geometries to an empty array.
 *
 * ## Update the detour properties
 *
 * To update the properties of the detour that are not related to the geometries (for example the title, description,
 * or any other property), call the {@link DetourEditor.updateDetourProperties} function and pass an object with the
 * properties to update:
 * ```js
 * detourEditor.updateDetourProperties({
 *   title: {"fr": "title fr", "de": "title de", "it": "title it", "en": "title en"},
 *   url: "https://www.suisse-rando.ch",
 * });
 * ```
 */
export default class DetourEditor extends EventTarget {
  private trackManager: TrackManager<unknown>;
  private uuid?: string;
  private lineType: DetourLineType = "closed_way_line";
  private view: View;
  private trackSource: VectorSource;
  private detourProperties: Record<string, unknown> = {};
  private onTrackChangeCallback: () => void;

  constructor(options: DetourEditorOptions) {
    super();

    this.uuid = options.uuid;

    this.view = options.map.getView();

    this.trackSource = new VectorSource();

    const trackLayer = new VectorLayer({
      source: this.trackSource,
      style: detourStyle,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });

    const shadowTrackLayer = new VectorLayer({
      source: new VectorSource(),
      //   style: shadowTrackStyle,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });
    options.map.addLayer(shadowTrackLayer);
    options.map.addLayer(trackLayer);
    const router = new GraphHopperRouter({
      map: options.map,
      url: "https://graphhopper-all.schweizmobil.ch/route?profile=schmwander&type=json&elevation=true&way_point_max_distance=0&instructions=false&points_encoded=true",
      maxRoutingTolerance: 15,
    });

    const densifier = new UnsnappedDensifier({
      distance: 10,
      maxPoints: 200,
    });
    const profiler = new FallbackProfiler({
      profilers: [
        new ExtractFromSegmentProfiler({
          projection: this.view.getProjection(),
        }),
        new SwisstopoProfiler({
          projection: this.view.getProjection(),
        }),
      ],
    });

    // FIXME: remove 'as VectorLayer<any>' when upgrading to OpenLayers 10
    this.trackManager = new TrackManager({
      map: options.map,
      router: router,
      profiler: profiler,
      trackLayer: trackLayer as VectorLayer<any>,
      shadowTrackLayer: shadowTrackLayer as VectorLayer<any>,
      densifier: densifier,
      style: detourStyle,
      deleteCondition: doubleClick,
      addLastPointCondition: singleClick, // we have to use single click otherwise the double click is not fired
      addControlPointCondition: doubleClick,
      hitTolerance: 15,
      switchPartOnDrag: false,
    });

    this.edit = true;

    // create a new part for the 'detour_line'
    this.trackManager.createNewPart();
    // switch back to the first part 'closed_way_line'
    this.trackManager.workOnPart(0);

    this.loadDetour();

    this.onTrackChangeCallback = this.onTrackChange.bind(this);
    this.trackManager.addTrackChangeEventListener(this.onTrackChangeCallback);
  }

  /**
   * Returns the uuid of the detour. If the detour is not saved yet, this function returns undefined.
   */
  public getUuid(): string | undefined {
    return this.uuid;
  }

  private async onTrackChange() {
    this.dispatchCustomEvent("change");
  }

  private async loadDetour() {
    this.edit = false;
    if (this.uuid) {
      this.detourProperties = {};

      const currentPart = this.trackManager.activePart();

      const detour = await fetchDetour(this.uuid);

      const closed_way_features = readClosedWayGeometries(detour, this.view.getProjection());
      this.trackManager.workOnPart(partByLineType.closed_way_line);
      await this.trackManager.restoreFeatures(closed_way_features);

      const detour_features = readDetourGeometries(detour, this.view.getProjection());
      this.trackManager.workOnPart(partByLineType.detour_line);
      await this.trackManager.restoreFeatures(detour_features);

      this.trackManager.workOnPart(currentPart);

      this.view.fit(this.trackSource.getExtent(), {
        padding: [30, 30, 30, 30],
      });
    }
    this.edit = true;
    this.dispatchEvent(new CustomEvent("detour-loaded"));
  }

  /**
   * Computes the properties (statistics) of a detour, including total time, distance, ascent and descent.
   *
   * @returns {DetourProperties}
   */
  public computeDetourProperties(): DetourProperties {
    const allSegments = this.trackManager.getAllSegments();

    const totals: DetourProperties = {
      closedWay: { time: 0, distance: 0, ascent: 0, descent: 0 },
      detour: { time: 0, distance: 0, ascent: 0, descent: 0 },
    };
    allSegments.forEach((segments: Feature<LineString>[], index: number) => {
      const durations = segments.map((segment: Feature<LineString>) => walkingTime(segment.get("profile"), "hiking"));
      const upDownHeights = segments.map((segment: Feature<LineString>) =>
        walkingAscentDescent(segment.get("profile")),
      );
      const distances: number[] = segments.map((segment: Feature<LineString>) => segment.get("profile").at(-1).at(3));

      const distance = distances.reduce((prev, curr) => prev + curr, 0);
      const ascent = upDownHeights.reduce((prev, curr) => prev + curr.ascent, 0);
      const descent = upDownHeights.reduce((prev, curr) => prev + curr.descent, 0);
      const time = durations.reduce((prev, curr) => prev + curr, 0);

      const workingPart = index === 0 ? "closedWay" : "detour";
      totals[workingPart] = { time, distance, ascent, descent };
    });

    return totals as DetourProperties;
  }

  private async dispatchCustomEvent(name: string) {
    const detourProperties = this.computeDetourProperties();
    this.dispatchEvent(new CustomEvent(name, { detail: detourProperties }));
  }

  public get detourLineType(): DetourLineType {
    return this.lineType;
  }

  public set detourLineType(value: DetourLineType) {
    if (!detourLineTypes.includes(value)) {
      throw new Error(`Invalid detour line type: '${value}', must be one of ${detourLineTypes.join(", ")}`);
    }
    this.lineType = value;
    this.trackManager.workOnPart(partByLineType[this.lineType]);
  }

  public get snapping(): boolean {
    return this.trackManager.snapping;
  }

  public set snapping(value: boolean) {
    this.trackManager.snapping = value;
  }

  public get edit(): boolean {
    return this.trackManager.mode === "edit";
  }

  public set edit(value: boolean) {
    this.trackManager.mode = value ? "edit" : "";
  }

  /**
   * Create or update the detour to the server
   */
  public async save(): Promise<string> {
    const currentPart = this.trackManager.activePart();

    this.trackManager.workOnPart(partByLineType.closed_way_line);
    const closed_way_line_geometries = writeClosedWayGeometries(
      this.trackManager.getSegments(),
      this.view.getProjection(),
    );
    this.trackManager.workOnPart(partByLineType.detour_line);
    const detour_line_geometries = writeDetourGeometries(this.trackManager.getSegments(), this.view.getProjection());
    this.trackManager.workOnPart(currentPart);

    const geometries = { ...closed_way_line_geometries, ...detour_line_geometries };

    if (this.uuid === undefined) {
      const { uuid } = await createDetour(geometries, this.detourProperties);
      this.uuid = uuid;
    } else {
      await updateDetour(this.uuid, geometries, this.detourProperties);
    }
    return this.uuid;
  }

  /**
   * Delete the currently selected line
   */
  public deleteLine(part: DetourLineType | undefined) {
    const index = part !== undefined ? partByLineType[part] : this.trackManager.activePart();

    // FIXME: We cannot call `this.trackManager.deletePart(index)`, because then the detour line becomes the closed_way line
    // Workaround: delete all control points until there are none
    let nControlPoints = this.trackManager.getControlPoints().length;
    while (nControlPoints > 0) {
      this.trackManager.deleteLastPoint();
      nControlPoints--;
    }
  }

  /**
   * Update the detour properties
   */
  public updateDetourProperties(properties: Record<string, unknown>) {
    this.detourProperties = properties;
  }

  private canUndo(): boolean {
    return this.trackManager.historyPosition >= 0;
  }

  private canRedo(): boolean {
    return this.trackManager.historyPosition < this.trackManager.historySize - 1;
  }

  /**
   * Undo the last action
   */
  public undo() {
    if (this.canUndo()) {
      this.trackManager.undo();
    }
  }

  /**
   * Redo the last action
   */
  public redo() {
    if (this.canRedo()) {
      this.trackManager.redo();
    }
  }
}
